// WishEditLayout — `/wishes/:id/edit-layout` route.
//
// Loads the full wish detail (`GET /api/v1/wishes/{id}`), renders the
// LayoutEditor with all 6 block types, and wires:
//
//   - PATCH /api/v1/wishes/{id}/blocks/{bid}        (BlockSettingsPanel save)
//   - POST  /api/v1/wishes/{id}/blocks              (AddBlockRow)
//   - DELETE /api/v1/wishes/{id}/blocks/{bid}        (BlockSettingsPanel delete)
//   - POST  /api/v1/wishes/{id}/blocks/reorder       (drag-reorder)
//   - PATCH /api/v1/wishes/{id}/completion-mode      (CompletionSettings)
//
// Per `docs/spec/wishlist/DESIGN.md §7` + DATA_MODEL §5.5 (auto-populate
// completion_sources for progress / budget / checklist blocks) +
// DATA_MODEL §8.3 (title required when same type has multiple blocks).
//
// Mobile long-press → BottomSheet reorder ships in W33.4.

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import { PageHero } from "@/components/primitives/PageHero";
import { PageErrorBoundary } from "@/components/ErrorBoundary";
import { LoadingState } from "@/components/primitives/LoadingState";
import { ErrorState } from "@/components/primitives/ErrorState";
import { useToast } from "@/components/ui/toast";
import { useHidePillNav } from "@/components/system/BottomChrome";
import {
  PageBottomActionBar,
  type PageBottomActionBarBack,
} from "@/components/system/PageBottomActionBar";
import { LayoutEditor } from "@/components/wish/LayoutEditor";
import { getWish, type WishOut } from "@/lib/api/wishlist";
import { useWishBlockMutations, WISH_QUERY_KEY_PREFIX } from "@/lib/hooks/wishBlockMutations";

export function WishEditLayoutPage() {
  // W50.0 B-1 moved the PillNav aside so it would not sit over the last rows;
  // W52.1 D-1 completed that move. Claiming the bottom edge is only half a
  // decision — the other half is that something occupies it. On its own the
  // claim left the bottom 400px of the page with **zero** controls, and the
  // page's own comment recorded that as a fact (`pb-32` and not `pb-40`:
  // "there is no docked bar here"). `dockedBarClaim.test.ts` now asserts the
  // converse of everything it asserted before: claiming ⇒ docking.
  useHidePillNav();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const wishId = id ?? "";

  // W50.3 D-2. **Success only** — failures were already reported app-wide
  // (`defaultMutationOnError`). Never add an `onError` here: the reporter reads
  // a call site that writes one as having taken responsibility. ADR-0158.
  const say = useCallback(
    (message: string) => toast.push({ message, variant: "you" }),
    [toast],
  );

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [...WISH_QUERY_KEY_PREFIX, wishId],
    queryFn: () => getWish(wishId),
    enabled: Boolean(wishId),
    staleTime: 30_000,
  });

  // Optimistic local edit — applied on top of server snapshot, so the
  // LayoutEditor can save block by block without round-tripping the
  // whole wish detail between each save.
  //
  // W49.0 §2. This used to be write-only: once an id was in here it stayed for
  // the life of the component, and because `wish` is built by layering it over
  // the server snapshot, no refetch could ever put the server's value back. So a
  // **failed** save still showed the user's number on screen — the amount they
  // had just typed, looking saved, with only a toast saying otherwise. They leave
  // the page, reload, and the money is gone. A toast does not undo a lie the UI
  // is still telling, and silently losing the thing someone typed is worse than
  // never having accepted it.
  //
  // Two exits now, which is what it was missing:
  //   - failure  → drop that id immediately, so the server value shows again
  //   - success  → drop it only once the refetched snapshot agrees (below),
  //                 so a successful save never flashes back to the old value
  const [localOverrides, setLocalOverrides] = useState<
    Record<string, { title: string; data: Record<string, unknown> }>
  >({});

  const clearOverride = useCallback((blockId: string) => {
    setLocalOverrides((prev) => {
      if (!(blockId in prev)) return prev;
      const next = { ...prev };
      delete next[blockId];
      return next;
    });
  }, []);

  const wish: WishOut | undefined = data
    ? {
        ...data,
        blocks: data.blocks.map((b) => {
          const override = localOverrides[b.id];
          if (!override) return b;
          return { ...b, title: override.title, data: override.data };
        }),
      }
    : undefined;

  // Success exit: an override has done its job once the server's own copy says
  // the same thing. Comparing values rather than invalidating on a timer is
  // what keeps this from being either premature (revert before the refetch) or
  // permanent (the original bug).
  useEffect(() => {
    if (!data) return;
    setLocalOverrides((prev) => {
      const ids = Object.keys(prev);
      if (ids.length === 0) return prev;
      const byId = new Map(data.blocks.map((b) => [b.id, b]));
      let changed = false;
      const next: typeof prev = {};
      for (const [id, override] of Object.entries(prev)) {
        const server = byId.get(id);
        if (
          server &&
          server.title === override.title &&
          JSON.stringify(server.data ?? {}) === JSON.stringify(override.data)
        ) {
          changed = true;
          continue;
        }
        next[id] = override;
      }
      return changed ? next : prev;
    });
  }, [data]);

  // W51 — the five block mutations moved to `lib/hooks/wishBlockMutations.ts`.
  // This file was 59 bytes under the 15 KB route ceiling before the dashboard
  // invalidation landed; the unit moved rather than the comments being shaved.
  const { saving, onSaveBlock, onAddBlock, onDeleteBlock, onReorder, onSetCompletion } =
    useWishBlockMutations({ wishId, say, clearOverride, setLocalOverrides });

  /**
   * W52.1 D-1 — the bar's two controls, and why they share a destination.
   *
   * 前往心願 is not a shortcut; it is what this page's only honest exit has
   * always been. The hero button this replaces was labelled 「儲存」 while doing
   * nothing of the kind: every block saves itself through
   * `useWishBlockMutations` (PATCH / POST / DELETE / reorder), and W39.53 had
   * already established that the heaviest button on the screen was "finish and
   * go back". Naming it 「儲存」 was the lie; naming it 「完成」 is the same
   * behaviour with an honest label.
   *
   * Both controls land on `/wishes/:id`, which is redundant by construction —
   * but `PageBottomActionBar`'s back slot is a component contract (W35.6: "leaving
   * the page is always one tap from the action you came to perform"), and six
   * create pages render that exact shape. Dropping the back would leave the bar's
   * left edge empty and make this the only one of seven that reads differently.
   */
  const leaveLayout = () => navigate(`/wishes/${wishId}`);
  const barBack: PageBottomActionBarBack = {
    onClick: leaveLayout,
    label: "返回",
    icon: ArrowLeft,
  };

  return (
    <PageErrorBoundary label="編輯心願">
      {/* W49.0 §6 — hero outside `PageContainer`; see `Wishes.tsx`.
          W52.1 D-1 — `pb-40` clears the bar this page now docks, matching the
          other six create/edit pages. It was `pb-32` precisely because nothing
          was there to clear. */}
      <div className="flex flex-col pb-40" data-testid="wish-edit-layout-page">
        <PageHero
          title={wish?.name ?? "編輯心願"}
          eyebrow="版面編輯"
          subtitle={wish ? `${wish.blocks.length} 個區塊` : undefined}
          trailing={
            /*
             * W52.1 D-1 — the 「儲存」 pill is **gone**, not duplicated. It is the
             * bar's primary now. Leaving it here as well would be one action with
             * two entrances, on a page where the label was already wrong.
             *
             * The hero's 返回 stays: it is at the top of a long scrolling screen,
             * and the bar's back is at the bottom of it.
             */
            <button
              type="button"
              onClick={leaveLayout}
              aria-label="返回"
              className="press inline-flex items-center justify-center gap-1.5 min-h-11 min-w-11 px-0 sm:px-s-4 rounded-full border border-hairline text-ink-1"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              <span className="hidden sm:inline">返回</span>
            </button>
          }
        />
        <PageContainer>
          {isLoading ? (
            <LoadingState shape="card" count={4} />
          ) : isError ? (
            <ErrorState
              title="讀取心願失敗"
              description={error instanceof Error ? error.message : "請稍後再試"}
              onRetry={() => refetch()}
            />
          ) : !wish ? (
            <ErrorState
              title="心願不存在"
              description="可能已被刪除"
              onRetry={() => navigate("/wishes")}
            />
          ) : (
            <LayoutEditor
              wish={wish}
              saving={saving}
              onSaveBlock={onSaveBlock}
              onAddBlock={onAddBlock}
              onDeleteBlock={onDeleteBlock}
              onReorder={onReorder}
              onSetCompletion={onSetCompletion}
            />
          )}
        </PageContainer>

        {/*
          W52.1 D-1 — `.action-pill` material (navy `rgb(31 37 54)`, 298×60),
          identical to `/calendar/events/new`. Not a new style: six create/edit
          pages already ship this exact bar, and this page was the seventh that
          had the bottom edge but not the bar.

          `disabled={!wish || saving}` is carried over unchanged from the hero
          button (W50.3 D-2): panel writes are fire-and-forget, so leaving
          mid-write loses the optimistic edit the page is still showing.
        */}
        <PageBottomActionBar
          primary={{
            label: "完成",
            onClick: leaveLayout,
            disabled: !wish || saving,
            testId: "layout-editor-finish",
          }}
          back={barBack}
          testId="wish-edit-layout-bottom-bar"
        />
      </div>
    </PageErrorBoundary>
  );
}
